import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, RefreshCw, TrendingDown, TrendingUp, AlertCircle, Wallet, Users, FileSpreadsheet } from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ComposedChart,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MES_LABELS_ES = {
  '01': 'Ene', '02': 'Feb', '03': 'Mar', '04': 'Abr', '05': 'May', '06': 'Jun',
  '07': 'Jul', '08': 'Ago', '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dic',
};

function formatMesLabel(mesIso) {
  // "2026-01" → "Ene 26"
  if (!mesIso) return '';
  const [y, m] = mesIso.split('-');
  return `${MES_LABELS_ES[m] || m} ${y.slice(-2)}`;
}

function formatARS(v) {
  const n = Number(v || 0);
  return n.toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });
}

export default function AdminDeudaEvolucionPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exportando, setExportando] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const cargar = async (force = false) => {
    const setter = force ? setRefreshing : setLoading;
    setter(true);
    try {
      const r = await axios.get(
        `${API_URL}/admin/estado-cuenta/stats-evolucion?meses=12${force ? '&force_rebuild=true' : ''}`,
        { headers: authHeaders }
      );
      setData(r.data);
      if (force) toast.success('Rollup recalculado');
    } catch (err) {
      toast.error('Error cargando estadísticas: ' + (err.response?.data?.detail || err.message));
    } finally {
      setter(false);
    }
  };

  const exportarXlsx = async () => {
    setExportando(true);
    try {
      const r = await axios.get(
        `${API_URL}/admin/estado-cuenta/exportar-xlsx`,
        { headers: authHeaders, responseType: 'blob', timeout: 60000 }
      );
      // Nombre del archivo: intentar leer del header, fallback con fecha
      let filename = 'estadisticas_estado_cuenta.xlsx';
      const cd = r.headers['content-disposition'] || r.headers['Content-Disposition'];
      if (cd) {
        const m = /filename="?([^"]+)"?/.exec(cd);
        if (m) filename = m[1];
      }
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel descargado');
    } catch (err) {
      toast.error('No se pudo generar el Excel: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExportando(false);
    }
  };

  useEffect(() => {
    cargar(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin" /> Cargando evolución de deuda...
      </div>
    );
  }

  if (!data || !data.meses || data.meses.length === 0) {
    return (
      <div className="p-8 max-w-3xl">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-700">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Sin datos aún
            </CardTitle>
            <CardDescription>
              Todavía no hay consultas de estado de cuenta registradas. El dashboard
              se activa automáticamente después del primer ciclo mensual (día 10).
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Fallback: si el rollup viejo no trae total_matriculades, usa socios_consultados.
  // Los meses recalculados post-2.59 traen el padrón histórico correcto.
  const baseRows = data.meses.map((m) => {
    const leve = m.deuda_leve || 0;
    const moderada = m.deuda_moderada || 0;
    const grave = m.deuda_grave || 0;
    const totalNiveles = leve + moderada + grave;
    const pct = (n) => (totalNiveles > 0 ? +((n / totalNiveles) * 100).toFixed(1) : 0);
    const consultados = m.socios_consultados || 0;
    const totalMatriculades = m.total_matriculades || consultados;
    const deudaTotal = m.deuda_total || 0;
    const deudaVencida = m.deuda_vencida_total || 0;
    const morosos = m.morosos || 0;
    const deudaPromedio = totalMatriculades > 0 ? +(deudaTotal / totalMatriculades).toFixed(2) : 0;
    const tasaMorosidad = totalMatriculades > 0 ? +((morosos / totalMatriculades) * 100).toFixed(1) : 0;
    const pctVencida = deudaTotal > 0 ? +((deudaVencida / deudaTotal) * 100).toFixed(1) : 0;
    return {
      mes: m.mes,
      mesLabel: formatMesLabel(m.mes),
      deuda_total: deudaTotal,
      deuda_vencida: deudaVencida,
      al_dia: m.al_dia,
      morosos: morosos,
      con_plan_pago: m.con_plan_pago,
      matriculades_consultados: consultados,
      total_matriculades: totalMatriculades,
      // Métricas relativas al padrón total
      deuda_promedio: deudaPromedio,
      tasa_morosidad: tasaMorosidad,
      pct_deuda_vencida: pctVencida,
      // Distribución por severidad
      leve_cant: leve,
      moderada_cant: moderada,
      grave_cant: grave,
      total_niveles: totalNiveles,
      leve_pct: pct(leve),
      moderada_pct: pct(moderada),
      grave_pct: pct(grave),
    };
  });

  // Regresión lineal simple para proyectar 3 meses hacia adelante.
  // y = a + b*x  donde x es el índice del mes (0..n-1)
  const linearFit = (values) => {
    const n = values.length;
    if (n < 2) return { a: values[0] || 0, b: 0 };
    let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
    for (let i = 0; i < n; i++) {
      sumX += i;
      sumY += values[i];
      sumXY += i * values[i];
      sumXX += i * i;
    }
    const denom = n * sumXX - sumX * sumX;
    const b = denom !== 0 ? (n * sumXY - sumX * sumY) / denom : 0;
    const a = (sumY - b * sumX) / n;
    return { a, b };
  };

  const proyectarMesLabel = (baseMes, offset) => {
    if (!baseMes) return '';
    const [y, m] = baseMes.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1 + offset, 1));
    const my = String(dt.getUTCFullYear()).slice(-2);
    const mm = MES_LABELS_ES[String(dt.getUTCMonth() + 1).padStart(2, '0')];
    return `${mm} ${my}`;
  };

  const buildRowsConProyeccion = (baseRows, key) => {
    // Devuelve `baseRows` + 3 filas futuras. Cada fila tiene:
    //  - [key]: valor real (solo pasado)
    //  - trend: valor de la línea de tendencia (todo el rango incl. futuro)
    //  - projection: valor futuro (solo 3 meses siguientes, y el último real como ancla)
    const values = baseRows.map((r) => r[key] || 0);
    const { a, b } = linearFit(values);
    const n = baseRows.length;
    const withTrend = baseRows.map((r, i) => ({
      ...r,
      trend: +(a + b * i).toFixed(2),
      projection: i === n - 1 ? r[key] : null, // ancla en el último real
    }));
    const ancla = baseRows[n - 1];
    for (let k = 1; k <= 3; k++) {
      const idx = n - 1 + k;
      const projValue = Math.max(0, +(a + b * idx).toFixed(2));
      withTrend.push({
        mes: `proj-${k}`,
        mesLabel: proyectarMesLabel(ancla?.mes, k),
        [key]: null,
        trend: +(a + b * idx).toFixed(2),
        projection: projValue,
        _es_proyeccion: true,
      });
    }
    return withTrend;
  };

  const rowsDeudaPromedio = buildRowsConProyeccion(baseRows, 'deuda_promedio');
  const rowsTasaMorosidad = buildRowsConProyeccion(baseRows, 'tasa_morosidad');
  const rows = baseRows; // alias original (usado por gráficos absolutos + severidad)

  const kpis = data.kpis || {};
  const variacionPct = kpis.variacion_pct || 0;
  const esReduccion = kpis.reduccion === true;
  const variacionAbs = kpis.variacion_absoluta || 0;
  const KpiIcon = esReduccion ? TrendingDown : TrendingUp;
  const kpiColor = esReduccion ? 'text-green-600' : 'text-red-600';
  const kpiBg = esReduccion ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';

  // Deltas mes-a-mes en cantidades absolutas (para chips en KPIs)
  const ultimoRow = baseRows[baseRows.length - 1] || {};
  const anteriorRow = baseRows[baseRows.length - 2] || null;
  const deltaMorosos = anteriorRow ? (ultimoRow.morosos || 0) - (anteriorRow.morosos || 0) : null;
  const deltaAlDia = anteriorRow ? (ultimoRow.al_dia || 0) - (anteriorRow.al_dia || 0) : null;
  const deltaTasaMorosidad = anteriorRow
    ? +(((ultimoRow.tasa_morosidad || 0) - (anteriorRow.tasa_morosidad || 0)).toFixed(1))
    : null;

  const DeltaChip = ({ value, unidad = '', invertirColores = false, testid }) => {
    if (value === null || value === undefined) return null;
    const positivo = value > 0;
    const negativo = value < 0;
    // "invertirColores": para métricas donde subir es malo (morosos, tasa) → rojo
    const buenoSubir = !invertirColores;
    const cls = value === 0
      ? 'bg-gray-100 text-gray-600'
      : (positivo === buenoSubir)
        ? 'bg-green-100 text-green-700'
        : 'bg-red-100 text-red-700';
    const signo = positivo ? '+' : '';
    return (
      <span
        className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium mt-1 ${cls}`}
        data-testid={testid}
      >
        {positivo && <TrendingUp className="w-2.5 h-2.5" />}
        {negativo && <TrendingDown className="w-2.5 h-2.5" />}
        {signo}{value}{unidad} vs mes ant.
      </span>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Evolución de Deuda</h1>
          <p className="text-sm text-gray-500 mt-1">
            Últimos {rows.length} meses · dedupe por matriculade (última consulta del mes) · métricas relativas al padrón de portal (matriculades con cuenta activa)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={exportarXlsx}
            disabled={exportando}
            data-testid="btn-exportar-xlsx"
          >
            {exportando
              ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generando...</>
              : <><FileSpreadsheet className="w-4 h-4 mr-1" /> Exportar Estadísticas (.xlsx)</>}
          </Button>
          <Button
            variant="outline"
            onClick={() => cargar(true)}
            disabled={refreshing}
            data-testid="btn-refresh-stats"
          >
            {refreshing
              ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Recalculando...</>
              : <><RefreshCw className="w-4 h-4 mr-1" /> Recalcular</>}
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-100 rounded-full">
                <Wallet className="w-5 h-5 text-slate-700" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Deuda mes actual ({formatMesLabel(kpis.mes_actual)})</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="kpi-deuda-actual">
                  {formatARS(kpis.deuda_mes_actual)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={`ring-1 ${kpiBg}`} data-testid="kpi-variacion-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-full ${esReduccion ? 'bg-green-100' : 'bg-red-100'}`}>
                <KpiIcon className={`w-5 h-5 ${kpiColor}`} />
              </div>
              <div>
                <p className="text-xs text-gray-500">Variación vs {formatMesLabel(kpis.mes_anterior) || 'mes anterior'}</p>
                <p className={`text-2xl font-bold ${kpiColor}`} data-testid="kpi-variacion-pct">
                  {variacionPct > 0 ? '+' : ''}{variacionPct}%
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {esReduccion ? 'Reducción' : 'Aumento'}: {formatARS(Math.abs(variacionAbs))}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-red-100 rounded-full">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Matriculades morosos</p>
                <p className="text-2xl font-bold text-red-700" data-testid="kpi-morosos">
                  {kpis.morosos_mes_actual}
                </p>
                <DeltaChip value={deltaMorosos} invertirColores testid="delta-morosos" />
                <DeltaChip
                  value={deltaTasaMorosidad}
                  unidad=" pp"
                  invertirColores
                  testid="delta-tasa-morosidad"
                />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-green-100 rounded-full">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Matriculades al día</p>
                <p className="text-2xl font-bold text-green-700" data-testid="kpi-al-dia">
                  {kpis.al_dia_mes_actual}
                </p>
                <DeltaChip value={deltaAlDia} testid="delta-al-dia" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráfico Compuesto: Evolución de deuda + Padrón (matriculades) */}
      <Card data-testid="chart-deuda-area">
        <CardHeader>
          <CardTitle className="text-lg">Evolución de la Deuda Total vs. Padrón del Portal</CardTitle>
          <CardDescription>
            Últimos {rows.length} meses · deuda en ARS (eje izq.) y cantidad de matriculades <strong>con cuenta activa en el portal</strong> a cierre de cada mes (eje der.). Los matriculades sin usuario no entran en el cálculo porque no tienen datos de deuda.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={340}>
            <ComposedChart data={rows} margin={{ top: 8, right: 24, left: 12, bottom: 8 }}>
              <defs>
                <linearGradient id="gDeuda" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#7c3aed" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef" />
              <XAxis dataKey="mesLabel" fontSize={12} />
              <YAxis
                yAxisId="left"
                fontSize={12}
                tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                fontSize={12}
                allowDecimals={false}
                tickFormatter={(v) => `${v}`}
              />
              <Tooltip
                formatter={(v, name) => {
                  if (name === 'total_matriculades') return [v, 'Padrón portal (matriculades c/ usuario)'];
                  if (name === 'deuda_total') return [formatARS(v), 'Deuda total'];
                  return [formatARS(v), 'Deuda vencida'];
                }}
                labelFormatter={(l) => `Mes: ${l}`}
              />
              <Legend
                formatter={(v) => {
                  if (v === 'deuda_total') return 'Deuda total (ARS)';
                  if (v === 'deuda_vencida') return 'Deuda vencida (ARS)';
                  if (v === 'total_matriculades') return 'Padrón portal (matriculades c/ cuenta)';
                  return v;
                }}
              />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="deuda_total"
                stroke="#7c3aed"
                strokeWidth={2}
                fill="url(#gDeuda)"
              />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="deuda_vencida"
                stroke="#ea580c"
                strokeWidth={2}
                fill="#ea580c"
                fillOpacity={0.06}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="total_matriculades"
                stroke="#0ea5e9"
                strokeWidth={2.5}
                strokeDasharray="5 3"
                dot={{ r: 3, fill: '#0ea5e9' }}
                activeDot={{ r: 5 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
          <p className="text-[11px] text-gray-400 mt-2">
            Tip: si la línea celeste (padrón del portal) sube al mismo ritmo que la deuda, el aumento es sólo por más usuarios. Si la deuda sube más rápido que la línea celeste, hay deterioro real.
          </p>
        </CardContent>
      </Card>

      {/* Métricas relativas (no crecen con el padrón) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Deuda Promedio por Matriculade */}
        <Card data-testid="chart-deuda-promedio">
          <CardHeader>
            <CardTitle className="text-lg">Deuda promedio por matriculade</CardTitle>
            <CardDescription>
              Deuda total ÷ matriculades con cuenta activa en el portal · muestra si el matriculade "típico" debe cada vez más (independiente del tamaño del padrón). Línea gris = tendencia + proyección 3 meses.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={rowsDeudaPromedio} margin={{ top: 8, right: 16, left: 12, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef" />
                <XAxis dataKey="mesLabel" fontSize={12} />
                <YAxis
                  fontSize={12}
                  tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  formatter={(v, name) => {
                    if (v === null || v === undefined) return ['—', name];
                    if (name === 'trend') return [formatARS(v), 'Tendencia'];
                    if (name === 'projection') return [formatARS(v), 'Proyección'];
                    return [formatARS(v), 'Deuda promedio'];
                  }}
                  labelFormatter={(l) => `Mes: ${l}`}
                />
                <Legend
                  formatter={(v) => {
                    if (v === 'deuda_promedio') return 'Deuda promedio por matriculade';
                    if (v === 'trend') return 'Tendencia';
                    if (v === 'projection') return 'Proyección 3 meses';
                    return v;
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="deuda_promedio"
                  stroke="#7c3aed"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: '#7c3aed' }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="trend"
                  stroke="#94a3b8"
                  strokeWidth={1.5}
                  strokeDasharray="3 3"
                  dot={false}
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="projection"
                  stroke="#7c3aed"
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  strokeOpacity={0.55}
                  dot={{ r: 3, fill: '#c4b5fd' }}
                  connectNulls
                  isAnimationActive={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Tasa de Morosidad */}
        <Card data-testid="chart-tasa-morosidad">
          <CardHeader>
            <CardTitle className="text-lg">Tasa de morosidad</CardTitle>
            <CardDescription>
              % de matriculades morosos sobre el padrón del portal del mes (excluye a los que no tienen cuenta activa). Línea gris = tendencia + proyección 3 meses.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={rowsTasaMorosidad} margin={{ top: 8, right: 16, left: 12, bottom: 8 }}>
                <defs>
                  <linearGradient id="gMorosidad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#dc2626" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#dc2626" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef" />
                <XAxis dataKey="mesLabel" fontSize={12} />
                <YAxis
                  fontSize={12}
                  domain={[0, 100]}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip
                  formatter={(v, name, item) => {
                    if (v === null || v === undefined) return ['—', name];
                    if (name === 'trend') return [`${v}%`, 'Tendencia'];
                    if (name === 'projection') return [`${v}%`, 'Proyección'];
                    const row = item?.payload || {};
                    return [`${v}% (${row.morosos ?? 0}/${row.total_matriculades ?? 0})`, 'Tasa de morosidad'];
                  }}
                  labelFormatter={(l) => `Mes: ${l}`}
                />
                <Legend
                  formatter={(v) => {
                    if (v === 'tasa_morosidad') return 'Tasa de morosidad (%)';
                    if (v === 'trend') return 'Tendencia';
                    if (v === 'projection') return 'Proyección 3 meses';
                    return v;
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="tasa_morosidad"
                  stroke="#dc2626"
                  strokeWidth={2.5}
                  fill="url(#gMorosidad)"
                  connectNulls={false}
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="trend"
                  stroke="#94a3b8"
                  strokeWidth={1.5}
                  strokeDasharray="3 3"
                  dot={false}
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="projection"
                  stroke="#dc2626"
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  strokeOpacity={0.55}
                  dot={{ r: 3, fill: '#fca5a5' }}
                  connectNulls
                  isAnimationActive={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Ratio: % de la deuda total que ya venció */}
      <Card data-testid="chart-pct-deuda-vencida">
        <CardHeader>
          <CardTitle className="text-lg">% de la deuda total que ya venció</CardTitle>
          <CardDescription>
            Deuda vencida ÷ deuda total · si sube, los matriculades arrastran mora más vieja; si baja, la mora es más reciente y recuperable.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={rows} margin={{ top: 8, right: 16, left: 12, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef" />
              <XAxis dataKey="mesLabel" fontSize={12} />
              <YAxis
                fontSize={12}
                domain={[0, 100]}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip
                formatter={(v, _n, item) => {
                  const row = item?.payload || {};
                  return [
                    `${v}% (${formatARS(row.deuda_vencida)} sobre ${formatARS(row.deuda_total)})`,
                    '% de la deuda vencida',
                  ];
                }}
                labelFormatter={(l) => `Mes: ${l}`}
              />
              <Legend formatter={() => '% de la deuda vencida'} />
              <Bar dataKey="pct_deuda_vencida" fill="#ea580c" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Gráfico de Barras Apiladas: Al día vs Morosos */}
      <Card data-testid="chart-al-dia-vs-morosos">
        <CardHeader>
          <CardTitle className="text-lg">Al día vs Morosos por mes</CardTitle>
          <CardDescription>Cantidad de matriculades consultados en cada mes</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={rows} margin={{ top: 8, right: 24, left: 12, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef" />
              <XAxis dataKey="mesLabel" fontSize={12} />
              <YAxis fontSize={12} allowDecimals={false} />
              <Tooltip
                formatter={(v, name) => [v, name === 'al_dia' ? 'Al día' : 'Morosos']}
                labelFormatter={(l) => `Mes: ${l}`}
              />
              <Legend
                formatter={(v) => (v === 'al_dia' ? 'Al día' : 'Morosos')}
              />
              <Bar dataKey="al_dia" stackId="a" fill="#10b981" />
              <Bar dataKey="morosos" stackId="a" fill="#dc2626" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Gráfico de Barras Apiladas 100%: Severidad de deuda (Leve / Moderada / Grave) */}
      <Card data-testid="chart-severidad-deuda">
        <CardHeader>
          <CardTitle className="text-lg">Distribución por severidad de deuda</CardTitle>
          <CardDescription>
            Porcentaje mensual de matriculades con deuda leve (≤ $50k), moderada ($50k–$150k) y grave (&gt; $150k) · sobre deuda vencida
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={340}>
            <BarChart
              data={rows}
              margin={{ top: 8, right: 24, left: 12, bottom: 8 }}
              stackOffset="expand"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eef" />
              <XAxis dataKey="mesLabel" fontSize={12} />
              <YAxis
                fontSize={12}
                tickFormatter={(v) => `${Math.round(v * 100)}%`}
                domain={[0, 1]}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || !payload.length) return null;
                  const row = payload[0].payload;
                  const nombres = {
                    leve_cant: { label: 'Leve', color: '#eab308', pct: row.leve_pct },
                    moderada_cant: { label: 'Moderada', color: '#f97316', pct: row.moderada_pct },
                    grave_cant: { label: 'Grave', color: '#dc2626', pct: row.grave_pct },
                  };
                  return (
                    <div className="bg-white border border-gray-200 rounded-md shadow-sm px-3 py-2 text-xs">
                      <div className="font-semibold text-gray-700 mb-1">Mes: {label}</div>
                      {payload.map((p) => {
                        const meta = nombres[p.dataKey];
                        if (!meta) return null;
                        return (
                          <div key={p.dataKey} className="flex items-center gap-2 py-0.5">
                            <span
                              className="inline-block w-2.5 h-2.5 rounded-sm"
                              style={{ background: meta.color }}
                            />
                            <span className="text-gray-600 w-20">{meta.label}:</span>
                            <span className="font-medium text-gray-900">
                              {p.value} matriculade{p.value === 1 ? '' : 's'}
                            </span>
                            <span className="text-gray-500">({meta.pct}%)</span>
                          </div>
                        );
                      })}
                      <div className="mt-1 pt-1 border-t border-gray-100 text-[11px] text-gray-500">
                        Total con deuda vencida: {row.total_niveles}
                      </div>
                    </div>
                  );
                }}
              />
              <Legend
                formatter={(v) => {
                  if (v === 'leve_cant') return 'Leve (≤ $50k)';
                  if (v === 'moderada_cant') return 'Moderada ($50k–$150k)';
                  if (v === 'grave_cant') return 'Grave (> $150k)';
                  return v;
                }}
              />
              <Bar dataKey="leve_cant" stackId="sev" fill="#eab308" />
              <Bar dataKey="moderada_cant" stackId="sev" fill="#f97316" />
              <Bar dataKey="grave_cant" stackId="sev" fill="#dc2626" />
            </BarChart>
          </ResponsiveContainer>
          <p className="text-[11px] text-gray-400 mt-2">
            Nota: los matriculades "al día" (sin deuda vencida) no se incluyen en este gráfico; ver el gráfico anterior para la distribución total.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
